import { useMutation } from '@apollo/client';
import { useState } from 'react';

import type { EngagementAmendmentChangeSetInput } from '@/__generated__/graphql';
import Button from '@/componentsv2/Button';
import { Drawer } from '@/componentsv2/Drawer';
import Input from '@/componentsv2/Input';
import { firstErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import { formatHours } from './money';
import { COUNTER_ENGAGEMENT_AMENDMENT, PROPOSE_ENGAGEMENT_AMENDMENT } from './operations';
import type { EngagementView } from './viewModel';

interface UpdateEngagementDrawerProps {
  open: boolean;
  onClose: () => void;
  view: EngagementView;
  /** Whether the viewer may set a rate — the expert's own, never the customer's. */
  canSetRate: boolean;
  /** Set when countering: the amendment being superseded. */
  counterOfAmendmentId?: string | null;
  /** Prefill when countering, parsed from the amendment being answered. */
  initial?: { nteHours?: string; scope?: string; rateDollars?: string };
}

/**
 * The only currency an amendment can carry.
 *
 * Not `view.currency`, which is a read-side value: the api accepts exactly one
 * denomination on the write side. `validateChangeSet` rejects a non-USD
 * `expert_rate` outright (`engagement/service/amendment.go:121`), as does every
 * pricing entry point it feeds (`pricing.go:424`, `:507`), and the input field
 * documents itself as USD (`engagement_amendment_gql.go:197`). All engagement
 * money is USD by construction — the funding method chooses how it is paid, not
 * what it is denominated in. Threading the read currency here would turn a
 * correct write into `amendment_invalid_change_set` rather than prevent one.
 *
 * Shared with the field label so the number sent and the number asked for cannot
 * drift.
 */
const AMENDMENT_CURRENCY = 'USD';

/**
 * Propose or counter an amendment.
 *
 * **Three fields, because three are amendable.** `EngagementAmendmentChangeSetInput`
 * carries `scope`, `nteHoursTotal` and `expertRate` — no dates, no pacing. Those
 * are offer-time terms with no amendable column, so they are absent here rather
 * than disabled (the MakeOfferDrawer doctrine: a surface describes only what it
 * can do).
 *
 * The rate field is expert-only. A customer-side proposer setting `expertRate`
 * would be setting the *expert's* underlying rate, which §6.3 puts on the other
 * side of the wall — and the api prices the customer's loaded rate from it
 * rather than taking one.
 */
export default function UpdateEngagementDrawer({
  open,
  onClose,
  view,
  canSetRate,
  counterOfAmendmentId = null,
  initial,
}: UpdateEngagementDrawerProps): JSX.Element {
  const [nteHours, setNteHours] = useState(initial?.nteHours ?? '');
  const [scope, setScope] = useState(initial?.scope ?? '');
  const [rateDollars, setRateDollars] = useState(initial?.rateDollars ?? '');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [propose, { loading: proposing }] = useMutation(PROPOSE_ENGAGEMENT_AMENDMENT, {
    refetchQueries: ['engagementHub'],
  });
  const [counter, { loading: countering }] = useMutation(COUNTER_ENGAGEMENT_AMENDMENT, {
    refetchQueries: ['engagementHub'],
  });
  const submitting = proposing || countering;

  const nte = Number.parseFloat(nteHours);
  const rate = Number.parseFloat(rateDollars);
  const hasNte = nteHours.trim() !== '';
  const hasRate = canSetRate && rateDollars.trim() !== '';
  const hasScope = scope.trim() !== '';

  /**
   * Client twin of `engagement.amendment_nte_below_confirmed`: the ceiling can
   * never drop below hours already confirmed, because those are already owed.
   */
  const nteError = !hasNte
    ? null
    : !Number.isFinite(nte) || nte <= 0
      ? 'Enter a positive number of hours.'
      : nte < view.nteHoursConsumed
        ? `This engagement has already confirmed ${formatHours(view.nteHoursConsumed)}.`
        : null;
  const rateError =
    hasRate && (!Number.isFinite(rate) || rate <= 0) ? 'Enter an hourly rate above zero.' : null;
  // Client twin of `engagement.amendment_invalid_change_set`.
  const nothingChanged = !hasNte && !hasScope && !hasRate;

  const canSubmit = !nothingChanged && !nteError && !rateError && !submitting;
  const isCounter = counterOfAmendmentId !== null;

  async function handleSubmit() {
    setTouched(true);
    if (!canSubmit) return;
    setError(null);
    // Keys are omitted rather than nulled, so an untouched field stays untouched.
    const changeSet: EngagementAmendmentChangeSetInput = {
      ...(hasNte ? { nteHoursTotal: nte.toFixed(2) } : {}),
      ...(hasScope ? { scope: scope.trim() } : {}),
      ...(hasRate
        ? { expertRate: { cents: String(Math.round(rate * 100)), currency: AMENDMENT_CURRENCY } }
        : {}),
    };
    try {
      if (isCounter) {
        await counter({
          variables: { input: { amendmentId: counterOfAmendmentId, changeSet } },
        });
      } else {
        await propose({ variables: { input: { engagementId: view.id, changeSet } } });
      }
      onClose();
    } catch (err) {
      setError(
        getDefaultErrorMessage(firstErrorCode(err) ?? '') ?? 'This amendment could not be sent.'
      );
    }
  }

  return (
    <Drawer open={open} onClose={onClose} size="sm">
      <Drawer.Header
        onClose={onClose}
        breadcrumbs={[{ title: isCounter ? 'Counter this amendment' : 'Propose an amendment' }]}
      />
      <Drawer.Main>
        <div className="flex flex-col gap-24">
          <p className="text-on-surface-muted body-2">
            Change only what needs to change. Anything you leave blank stays as it is.
          </p>

          <Input
            label="Not-to-exceed hours"
            value={nteHours}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setTouched(true);
              setNteHours(e.target.value);
            }}
            error={touched && nteError !== null}
            helperText={touched && nteError ? nteError : ''}
          />

          <Input
            label="Scope of work"
            multiline
            rows={4}
            value={scope}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setTouched(true);
              setScope(e.target.value);
            }}
            helperText="Recorded on the audit trail."
          />

          {canSetRate ? (
            <Input
              label={`Your hourly rate (${AMENDMENT_CURRENCY})`}
              value={rateDollars}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTouched(true);
                setRateDollars(e.target.value);
              }}
              error={touched && rateError !== null}
              helperText={touched && rateError ? rateError : ''}
            />
          ) : null}

          {touched && nothingChanged ? (
            <p className="text-red-600 body-2">
              Change at least one thing to propose an amendment.
            </p>
          ) : null}
          {error ? <p className="text-red-600 body-2">{error}</p> : null}
        </div>
      </Drawer.Main>
      <div className="flex justify-end gap-12 border-t border-surface-border p-24">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => void handleSubmit()} disabled={submitting}>
          {isCounter ? 'Send counter' : 'Propose amendment'}
        </Button>
      </div>
    </Drawer>
  );
}
